import { ArrivedButton } from '@/components/donation/ArrivedButton'
import { CallDonorButton } from '@/components/donation/CallDonorButton'
import { HeadOutButton } from '@/components/donation/HeadOutButton'
import { MarkCompleteButton } from '@/components/donation/MarkCompleteButton'
import { StatusBadge } from '@/components/donation/StatusBadge'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { StandardDonationAasmState } from '@types'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styles from './StopCard.module.scss'

export type StopStatus = 'dispatch' | 'return' | StandardDonationAasmState

export interface StopCardProps {
  stepNumber: number
  status: StopStatus
  donationId?: number
  name?: string
  address: string
  specification?: string
  isReported?: boolean
  isLast?: boolean
  phone?: string
  isFutureDate?: boolean
}

export const StopCard = ({
  stepNumber,
  status,
  donationId,
  name,
  address,
  specification,
  isReported = false,
  isLast = false,
  phone,
  isFutureDate = false,
}: StopCardProps) => {
  const navigate = useNavigate()
  const { partOfDay } = useParams<{ partOfDay: string }>()
  const [isExpanded, setIsExpanded] = useState(false)

  const handleDetails = () => {
    if (donationId) navigate(`/${partOfDay}/${donationId}`)
  }

  const isDispatch = status === 'dispatch'
  const isReturn = status === 'return'
  const isCompleted = status === StandardDonationAasmState.COMPLETED
  const isCanceled = status === StandardDonationAasmState.CANCELED
  const isPending = status === StandardDonationAasmState.ENQUEUED
  const isAccepted = status === StandardDonationAasmState.ACCEPTED
  const isEnRoute = status === StandardDonationAasmState.EN_ROUTED
  const isArrived = status === StandardDonationAasmState.ARRIVED

  const cardClasses = [
    styles.card,
    isCompleted && styles.cardCompleted,
    isCanceled && styles.cardCanceled,
    (isPending || isAccepted || isEnRoute || isArrived) &&
      isExpanded &&
      styles.cardActive,
    (isDispatch || isReturn) && styles.cardNotExpandable,
  ]
    .filter(Boolean)
    .join(' ')

  const chevronClasses = [styles.chevron, isExpanded && styles.chevronExpanded]
    .filter(Boolean)
    .join(' ')

  const handleToggle = () => {
    if (isDispatch || isReturn) return
    setIsExpanded(!isExpanded)
  }

  const renderBadges = () => {
    const badges = [<StatusBadge key="status" status={status} />]

    if (isReported) {
      badges.push(
        <Badge
          key="reported"
          text="Reported"
          variant="warning"
          icon="flag-report"
        />
      )
    }

    return badges
  }

  const showQuickActions =
    !isExpanded && !isDispatch && !isReturn && !isCompleted && !isCanceled
  const showExpandedContent = !isDispatch && !isReturn

  const expandedContentClasses = [
    styles.expandedContent,
    isExpanded && styles.expandedContentVisible,
  ]
    .filter(Boolean)
    .join(' ')

  const wrapperClasses = [styles.wrapper, isLast && styles.lastWrapper]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={wrapperClasses}
      data-testid={`stop-card-wrapper-${stepNumber}`}
    >
      <div
        className={cardClasses}
        onClick={handleToggle}
        data-testid={`stop-card-${stepNumber}`}
      >
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span
              className={styles.stepNumber}
              data-testid={`stop-card-step-${stepNumber}`}
            >
              {stepNumber}
            </span>
            <div
              className={styles.badges}
              data-testid={`stop-card-badges-${stepNumber}`}
            >
              {renderBadges()}
            </div>
          </div>
          {!isDispatch && !isReturn && (
            <Icon name="chevron-down" size={20} className={chevronClasses} />
          )}
        </div>

        <div className={styles.content}>
          <div className={styles.info}>
            {name && (
              <p
                className={styles.name}
                data-testid={`stop-card-name-${stepNumber}`}
              >
                {name}
              </p>
            )}
            <p
              className={styles.address}
              data-testid={`stop-card-address-${stepNumber}`}
            >
              {address}
            </p>
          </div>

          {showQuickActions && (
            <div
              className={styles.quickActions}
              onClick={(e) => e.stopPropagation()}
              data-testid={`stop-card-quick-actions-${stepNumber}`}
            >
              {!isPending && !isCanceled && !isCompleted && (
                <CallDonorButton phone={phone} iconOnly />
              )}
              {isAccepted && donationId && (
                <HeadOutButton
                  address={address}
                  donationId={donationId}
                  iconOnly
                  disabled={isFutureDate}
                />
              )}
              {isEnRoute && donationId && (
                <ArrivedButton
                  donationId={donationId}
                  iconOnly
                  disabled={isFutureDate}
                />
              )}
              {isArrived && donationId && (
                <MarkCompleteButton
                  donationId={donationId}
                  iconOnly
                  disabled={isFutureDate}
                />
              )}
            </div>
          )}
        </div>

        {showExpandedContent && (
          <div
            className={expandedContentClasses}
            data-testid={`stop-card-expanded-${stepNumber}`}
          >
            <div className={styles.expandedContentInner}>
              {specification && (
                <>
                  <p className={styles.specLabel}>Donation Items</p>
                  <p
                    className={styles.specText}
                    data-testid={`stop-card-spec-${stepNumber}`}
                  >
                    {specification}
                  </p>
                </>
              )}
              <div
                className={styles.expandedActions}
                onClick={(e) => e.stopPropagation()}
                data-testid={`stop-card-actions-${stepNumber}`}
              >
                <div className={styles.actionRow}>
                  <Button
                    text="Details"
                    variant="default"
                    onClick={handleDetails}
                    testId={`stop-card-details-button-${stepNumber}`}
                    className={
                      isCanceled || isCompleted || isPending
                        ? styles.headOutButton
                        : styles.detailsButton
                    }
                  />
                  {!isPending && !isCanceled && !isCompleted && (
                    <CallDonorButton
                      phone={phone}
                      className={styles.callButton}
                    />
                  )}
                </div>
                {isAccepted && donationId && (
                  <HeadOutButton
                    address={address}
                    donationId={donationId}
                    disabled={isFutureDate}
                    className={styles.headOutButton}
                  />
                )}
                {isEnRoute && donationId && (
                  <ArrivedButton
                    donationId={donationId}
                    disabled={isFutureDate}
                    className={styles.headOutButton}
                  />
                )}
                {isArrived && donationId && (
                  <MarkCompleteButton
                    donationId={donationId}
                    disabled={isFutureDate}
                    className={styles.headOutButton}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
